import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ArrowPathIcon,
  CheckIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { Button } from '@cottage/design-system/internal-tooling/ui'
import { SearchInput } from '@/tableControls'
import {
  deletePurchasingPackValue,
  getPurchasingPackValueUsage,
  mergePurchasingPackValues,
  patchPurchasingPackValue,
} from '@/services/purchasing'
import type {
  PurchasingPackValueRow,
  PurchasingPackValueUsageResponse,
} from '@/services/purchasingTypes'
import { filterPackValuesBySearch } from '@/pages/purchasing/purchasingPackSelectHelpers'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'

export type PurchasingPackValuesListProps = {
  values: PurchasingPackValueRow[]
  loading?: boolean
  canEdit: boolean
  onRefresh: () => Promise<void>
}

type DeleteMode = 'replace' | 'remove'

export default function PurchasingPackValuesList({
  values,
  loading = false,
  canEdit,
  onRefresh,
}: PurchasingPackValuesListProps) {
  const [searchFilter, setSearchFilter] = useState('')
  const [selectedValues, setSelectedValues] = useState<Set<string>>(new Set())
  const [editingValue, setEditingValue] = useState<string | null>(null)
  const [editedValue, setEditedValue] = useState('')
  const [busy, setBusy] = useState(false)

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [valueToDelete, setValueToDelete] = useState<string | null>(null)
  const [deleteMode, setDeleteMode] = useState<DeleteMode>('replace')
  const [deleteReplacement, setDeleteReplacement] = useState('')
  const [usage, setUsage] = useState<PurchasingPackValueUsageResponse | null>(null)

  const [showMergeModal, setShowMergeModal] = useState(false)
  const [mergeTarget, setMergeTarget] = useState('')

  const editInputRef = useRef<HTMLInputElement>(null)

  const filteredValues = filterPackValuesBySearch(values, searchFilter)
  const replacementOptions = values.filter((row) => row.value !== valueToDelete)

  useEffect(() => {
    if (!editingValue || !editInputRef.current) return
    editInputRef.current.focus()
    editInputRef.current.select()
  }, [editingValue])

  const handleToggleSelect = (value: string) => {
    setSelectedValues((prev) => {
      const next = new Set(prev)
      if (next.has(value)) next.delete(value)
      else next.add(value)
      return next
    })
  }

  const handleStartEdit = (value: string) => {
    setEditingValue(value)
    setEditedValue(value)
  }

  const handleCancelEdit = () => {
    setEditingValue(null)
    setEditedValue('')
  }

  const handleSaveEdit = async () => {
    if (!editingValue || !editedValue.trim() || !canEdit) return
    if (editedValue.trim() === editingValue) {
      handleCancelEdit()
      return
    }
    setBusy(true)
    try {
      const result = await patchPurchasingPackValue(editingValue, editedValue.trim())
      toast.success(`Updated ${result.updated_count} record(s)`)
      handleCancelEdit()
      await onRefresh()
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to rename pack value'))
    } finally {
      setBusy(false)
    }
  }

  const handleDeleteClick = async (value: string) => {
    if (!canEdit) return
    setValueToDelete(value)
    setDeleteMode('replace')
    setDeleteReplacement('')
    setUsage(null)
    setShowDeleteModal(true)
    try {
      const count = await getPurchasingPackValueUsage(value)
      setUsage(count)
      const others = values.filter((row) => row.value !== value)
      setDeleteReplacement(others[0]?.value ?? '')
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to load usage'))
    }
  }

  const handleConfirmDelete = async () => {
    if (!valueToDelete || !canEdit) return
    if (deleteMode === 'replace' && !deleteReplacement) {
      toast.error('Choose a replacement pack value')
      return
    }
    setBusy(true)
    try {
      const result = await deletePurchasingPackValue(
        valueToDelete,
        deleteMode === 'replace' ? deleteReplacement : null
      )
      toast.success(`Updated ${result.updated_count} record(s)`)
      setShowDeleteModal(false)
      setValueToDelete(null)
      setSelectedValues((prev) => {
        const next = new Set(prev)
        next.delete(valueToDelete)
        return next
      })
      await onRefresh()
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to delete pack value'))
    } finally {
      setBusy(false)
    }
  }

  const handleMergeClick = () => {
    if (selectedValues.size < 2 || !canEdit) return
    const selectedArray = Array.from(selectedValues)
    setMergeTarget(selectedArray[0] ?? '')
    setShowMergeModal(true)
  }

  const handleConfirmMerge = async () => {
    if (!mergeTarget || selectedValues.size < 2 || !canEdit) return
    setBusy(true)
    try {
      const result = await mergePurchasingPackValues(Array.from(selectedValues), mergeTarget)
      toast.success(`Updated ${result.updated_count} record(s)`)
      setShowMergeModal(false)
      setSelectedValues(new Set())
      setMergeTarget('')
      await onRefresh()
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to merge pack values'))
    } finally {
      setBusy(false)
    }
  }

  const usageSummary = useCallback((row: PurchasingPackValueUsageResponse | null) => {
    if (!row) return null
    const parts: string[] = []
    if (row.vendor_item_count > 0) {
      parts.push(
        `${row.vendor_item_count} vendor item${row.vendor_item_count !== 1 ? 's' : ''}`
      )
    }
    if (row.pack_version_count > 0) {
      parts.push(
        `${row.pack_version_count} pack version${row.pack_version_count !== 1 ? 's' : ''}`
      )
    }
    if (row.alias_count > 0) {
      parts.push(`${row.alias_count} alias${row.alias_count !== 1 ? 'es' : ''}`)
    }
    return parts
  }, [])

  return (
    <>
      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="shrink-0 border-b border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex items-center gap-2">
            <h2 className="truncate text-sm font-semibold text-gray-900">Pack values</h2>
            <span className="shrink-0 rounded bg-gray-200 px-1.5 py-0.5 text-xs text-gray-500">
              {values.length}
            </span>
            {loading ? (
              <ArrowPathIcon className="ml-auto h-4 w-4 animate-spin text-gray-400" aria-hidden />
            ) : null}
          </div>
        </div>

        <div className="shrink-0 border-b border-gray-100 px-3 py-2">
          <SearchInput
            value={searchFilter}
            onChange={setSearchFilter}
            placeholder="Search…"
            className="max-w-none"
            inputClassName="h-8 py-1.5 text-sm"
          />

          {canEdit && selectedValues.size >= 2 ? (
            <div className="mt-2 flex items-center gap-2">
              <Button gradient size="sm" disabled={busy} onClick={handleMergeClick}>
                Merge ({selectedValues.size})
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelectedValues(new Set())}>
                Clear
              </Button>
            </div>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {filteredValues.length === 0 ? (
            <p className="py-8 text-center text-sm italic text-gray-400">
              {values.length === 0 ? 'No pack values yet' : 'No matches found'}
            </p>
          ) : (
            <div className="divide-y divide-gray-50">
              {filteredValues.map((row) => (
                <div
                  key={row.value}
                  className={`group flex items-center gap-2 px-3 py-[3px] transition-colors ${
                    selectedValues.has(row.value) ? 'bg-gray-100' : 'hover:bg-gray-50'
                  }`}
                >
                  {canEdit ? (
                    <button
                      type="button"
                      onClick={() => handleToggleSelect(row.value)}
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                        selectedValues.has(row.value)
                          ? 'border-gray-700 bg-gray-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                      aria-label={`Select ${row.value}`}
                    >
                      {selectedValues.has(row.value) ? (
                        <CheckIcon className="h-2.5 w-2.5 text-white" aria-hidden />
                      ) : null}
                    </button>
                  ) : null}

                  {editingValue === row.value && canEdit ? (
                    <div className="flex flex-1 items-center gap-1">
                      <input
                        ref={editInputRef}
                        type="text"
                        value={editedValue}
                        onChange={(e) => setEditedValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void handleSaveEdit()
                          if (e.key === 'Escape') handleCancelEdit()
                        }}
                        className="input flex-1 py-0.5 text-sm"
                      />
                      <Button variant="ghost" size="sm" disabled={busy} onClick={() => void handleSaveEdit()}>
                        Save
                      </Button>
                      <Button variant="ghost" size="sm" iconOnly aria-label="Cancel edit" onClick={handleCancelEdit}>
                        <XMarkIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-gray-900">{row.value}</span>
                      </div>
                      {canEdit ? (
                        <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                          <Button
                            variant="ghost"
                            size="sm"
                            iconOnly
                            aria-label={`Edit ${row.value}`}
                            disabled={busy}
                            onClick={() => handleStartEdit(row.value)}
                          >
                            <PencilIcon className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            iconOnly
                            aria-label={`Delete ${row.value}`}
                            disabled={busy}
                            onClick={() => void handleDeleteClick(row.value)}
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : null}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showDeleteModal && valueToDelete ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowDeleteModal(false)}
            aria-hidden="true"
          />
          <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Delete &ldquo;{valueToDelete}&rdquo;?</h3>

            {usage && usageSummary(usage) && usageSummary(usage)!.length > 0 ? (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <p className="text-sm text-amber-800">
                  This value is used by {usageSummary(usage)!.join(', ')}.
                </p>
              </div>
            ) : null}

            <p className="mt-4 text-sm text-gray-700">Choose what happens to items using this value:</p>

            <div className="mt-3 space-y-3">
              <label className="flex items-start gap-2 rounded-lg border border-gray-200 p-3">
                <input
                  type="radio"
                  name="deleteOption"
                  checked={deleteMode === 'replace'}
                  onChange={() => setDeleteMode('replace')}
                  className="mt-1"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Replace with another value</p>
                  {deleteMode === 'replace' ? (
                    <select
                      value={deleteReplacement}
                      onChange={(e) => setDeleteReplacement(e.target.value)}
                      className="input mt-2 w-full text-sm"
                    >
                      {replacementOptions.map((row) => (
                        <option key={row.value} value={row.value}>
                          {row.value}
                        </option>
                      ))}
                    </select>
                  ) : null}
                </div>
              </label>

              <label className="flex items-start gap-2 rounded-lg border border-gray-200 p-3">
                <input
                  type="radio"
                  name="deleteOption"
                  checked={deleteMode === 'remove'}
                  onChange={() => setDeleteMode('remove')}
                  className="mt-1"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Remove without replacement</p>
                  <p className="mt-1 text-xs text-gray-500">
                    The pack value will be cleared on all vendor items and pack versions.
                  </p>
                </div>
              </label>
            </div>

            <div className="mt-6 flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </Button>
              <Button
                gradient
                className="flex-1"
                disabled={busy || (deleteMode === 'replace' && !deleteReplacement)}
                onClick={() => void handleConfirmDelete()}
              >
                Confirm delete
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {showMergeModal && selectedValues.size >= 2 ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowMergeModal(false)}
            aria-hidden="true"
          />
          <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">
              Merge {selectedValues.size} pack values
            </h3>
            <p className="mt-2 text-sm text-gray-600">Choose the target value to keep:</p>
            <div className="mt-4 space-y-2">
              {Array.from(selectedValues).map((value) => (
                <label
                  key={value}
                  className="flex items-center gap-2 rounded-lg border border-gray-200 p-3"
                >
                  <input
                    type="radio"
                    name="mergeTarget"
                    checked={mergeTarget === value}
                    onChange={() => setMergeTarget(value)}
                  />
                  <span className="text-sm text-gray-900">{value}</span>
                </label>
              ))}
            </div>
            <div className="mt-6 flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowMergeModal(false)}>
                Cancel
              </Button>
              <Button
                gradient
                className="flex-1"
                disabled={busy || !mergeTarget}
                onClick={() => void handleConfirmMerge()}
              >
                Confirm merge
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
